import type { GroupInvitePreviewDto, GroupJoinResultDto } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChatStore } from '../lib/chat-context';

const TOKEN = /^[A-Za-z0-9_-]{32,64}$/;

/** `/join#<token>`: shows the group (name and size only) and joins or sends a join request. */
export function JoinGroupPage() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { hash } = useLocation();
  const navigate = useNavigate();
  const store = useChatStore();
  const token = hash.slice(1);
  const valid = TOKEN.test(token);
  const [preview, setPreview] = useState<GroupInvitePreviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!valid) return;
    let alive = true;
    api<GroupInvitePreviewDto>('POST', '/group-invites/preview', { token }).then(
      (p) => alive && setPreview(p),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [token, valid, t]);

  const open = async (conversationId: string) => {
    await store?.loadConversations();
    navigate(`/messages/${conversationId}`, { replace: true });
  };

  const join = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<GroupJoinResultDto>('POST', '/group-invites/join', { token });
      if (r.status === 'joined' && r.conversationId) await open(r.conversationId);
      else setRequested(true);
    } catch (err) {
      setError(t(errorMessageKey(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="content-inner content-narrow">
      <div className="card stack" style={{ maxWidth: 480, margin: '0 auto' }}>
        <h1 style={{ fontSize: 'var(--text-lg)' }}>{preview?.title ?? t('join.title')}</h1>
        {!valid && <p role="alert">{t('join.missingToken')}</p>}
        {error && (
          <p role="alert" className="alert alert-error">
            {error}
          </p>
        )}
        {valid && !preview && !error && <p className="muted">{t('app.loading')}</p>}
        {preview && (
          <>
            {preview.description && <p>{preview.description}</p>}
            <p className="muted">{t('group.memberCount', { count: preview.memberCount })}</p>
            {preview.conversationId ? (
              <button className="btn btn-primary" type="button" onClick={() => void open(preview.conversationId!)}>
                {t('join.open')}
              </button>
            ) : requested || preview.requested ? (
              <p role="status">{t('join.requested')}</p>
            ) : (
              <>
                {preview.requireApproval && <p>{t('join.approvalNote')}</p>}
                <p className="hint">{t('join.privacyNote')}</p>
                <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void join()}>
                  {preview.requireApproval ? t('join.request') : t('join.join')}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
